import React from "react";
import { InfoTip } from "@/components/InfoTip";
import { Link } from "react-router-dom";
import { Coins, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBilling } from "@/contexts/BillingContext";

const MAX_BILLABLE_CHARS = 800000;
const PREMIUM_MODELS = ["gpt-5.2", "claude-sonnet-4-5"];

const baseCredits = (billing, chars) =>
  Math.max(1, Math.ceil(Math.min(chars, MAX_BILLABLE_CHARS) / (billing.chars_per_credit || 60000)));

const estimateMessage = (billing, credits, chars, mode, premium, t, chapterCredits) => {
  const suffix = premium ? ` ${t("credits_estimate.premium")}` : "";
  if (billing.is_admin) return t("trial.estimate_admin");
  if (billing.free_trial_open === false && !billing.is_pro && !credits && !billing.is_unlimited)
    return t("trial.estimate_closed");
  if (billing.is_unlimited && billing.unlimited_used < billing.unlimited_cap)
    return t("trial.estimate_unlimited", { used: billing.unlimited_used, cap: billing.unlimited_cap });
  if (mode === "chapters") {
    const n = chapterCredits || Math.max(1, Math.ceil(chars / (billing.chars_per_credit || 60000))) * (premium && chars <= 100000 ? 2 : 1);
    return t("credits_estimate.chapters", { need: n }) + suffix;
  }
  const base = baseCredits(billing, chars);
  const need = base * (premium ? 2 : 1);
  const freeLeft = Math.max(0, billing.free_summary_limit - billing.free_summaries_used);
  if (billing.is_pro && billing.monthly_summaries_used < billing.monthly_quota) return t("credits_estimate.pro_included");
  if (!billing.is_pro && base === 1 && freeLeft > 0) return t("credits_estimate.free_trial");
  if (billing.is_anonymous)
    return chars > 60000
      ? t("credits_estimate.anon_long", { need: base })
      : t("credits_estimate.anon_trial_used");
  return t("credits_estimate.cost", { need, count: need, credits }) + suffix;
};

// Shown under the text box: how many credits this summary will use, before launching it.
export function CreditEstimate({ chars, mode, model, chapterCredits }) {
  const { t } = useTranslation();
  const { billing, credits, freeAccess } = useBilling();
  if (!billing || chars < 200) return null;

  const premium = PREMIUM_MODELS.includes(model) && !billing.is_anonymous;
  const need = baseCredits(billing, chars) * (premium ? 2 : 1);
  const short = !freeAccess && !billing.is_anonymous && mode !== "chapters" && !billing.is_pro && need > 1 && credits < need;
  const truncated = mode !== "chapters" && chars > MAX_BILLABLE_CHARS;

  return (
    <div className="px-6 md:px-8 py-3 border-t border-border space-y-2 text-[13px]" data-testid="credit-estimate">
      <div className="flex items-start gap-2 text-foreground/80">
        <Coins className="w-4 h-4 mt-0.5 text-[#002FA7] flex-shrink-0" />
        <span data-testid="credit-estimate-text">{estimateMessage(billing, credits, chars, mode, premium, t, chapterCredits)}</span>
        <InfoTip testid="credit-estimate-info" content={t("tips.credits", "Le coût dépend de la longueur du texte : 1 crédit pour environ 20 pages avec Gemini 3 Flash, le double avec Claude ou GPT. Si le résumé échoue, rien n'est débité.")} />
        {short && (
          <Link to="/billing/upgrade" className="ml-auto underline text-[#002FA7] whitespace-nowrap" data-testid="credit-estimate-topup">
            {t("credits_estimate.topup")}
          </Link>
        )}
      </div>
      {truncated && (
        <div
          className="flex items-start gap-2 border border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] p-2"
          data-testid="credit-estimate-truncation"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{t("credits_estimate.truncated")}</span>
        </div>
      )}
    </div>
  );
}

export default CreditEstimate;
